import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  CalendarCheck,
  Table2,
  Calendar,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  Clock,
  Users,
  CheckCircle,
  XCircle,
  Loader2,
  UserCheck,
  BarChart3
} from 'lucide-react';
import { useRestaurantAuth } from './context/RestaurantAuthContext';
import { useRestaurantDatabase } from './context/RestaurantDatabaseContext';
import { useRealtimeBookings } from './hooks/useRealtimeBookings';
import { useBookingActions } from './hooks/useBookingActions';
import { RestaurantTranslationProvider, useRestaurantTranslation } from './context/RestaurantTranslationContext';
import LiveIndicator from './components/LiveIndicator';
import { Booking, BookingStatus } from './types/booking';

function RestaurantLayoutInner() {
  const { staff, restaurantName, logout } = useRestaurantAuth();
  const { database, hotelId, restaurantId } = useRestaurantDatabase();
  const { t, language } = useRestaurantTranslation();
  const location = useLocation();

  const { bookings, isConnected, newBookingAlert, clearNewBookingAlert } = useRealtimeBookings(
    database,
    hotelId,
    restaurantId,
    { includeCompleted: true }
  );

  const { updateBookingStatus, deleteBooking } = useBookingActions();

  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showNewBookingToast, setShowNewBookingToast] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const navigate = useNavigate();

  const basePath = `/restaurant/${hotelId}/${restaurantId}`;

  // Pending bookings
  const pendingBookings = useMemo(() => {
    return bookings.filter(b => b.status === 'pending').sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date);
      if (dateCompare !== 0) return dateCompare;
      return a.time.localeCompare(b.time);
    });
  }, [bookings]);

  const navItems = [
    { icon: LayoutDashboard, label: t.today, path: `${basePath}/dashboard`, showMobile: true },
    { icon: UserCheck, label: t.currentBookings || 'Current', path: `${basePath}/current`, showMobile: true },
    { icon: CalendarCheck, label: t.bookings, path: `${basePath}/bookings`, showMobile: true },
    { icon: Table2, label: t.tables, path: `${basePath}/tables`, showMobile: false },
    { icon: Calendar, label: t.calendar, path: `${basePath}/calendar`, showMobile: true },
    { icon: BarChart3, label: t.statistics, path: `${basePath}/statistics`, showMobile: true },
    { icon: Settings, label: t.settings, path: `${basePath}/settings`, showMobile: false },
  ];

  // Handle pending booking actions
  const handlePendingAction = async (bookingId: string, action: 'confirm' | 'cancel') => {
    setActionLoading(bookingId);
    try {
      if (action === 'confirm') {
        await updateBookingStatus(bookingId, 'confirmed');
      } else {
        await updateBookingStatus(bookingId, 'cancelled');
      }
      if (pendingBookings.length <= 1) {
        setShowPendingModal(false);
      }
    } catch (error) {
      console.error('Error updating booking:', error);
      alert(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle new booking alert
  useEffect(() => {
    if (newBookingAlert) {
      setShowNewBookingToast(true);
      const timer = setTimeout(() => {
        setShowNewBookingToast(false);
        clearNewBookingAlert();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [newBookingAlert, clearNewBookingAlert]);

  // ── Touch gesture support (swipe to open/close menu) ──
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      time: Date.now(),
    };
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const { x: startX, y: startY, time: startTime } = touchStartRef.current;
    const endX = e.changedTouches[0].clientX;
    const endY = e.changedTouches[0].clientY;
    const deltaX = endX - startX;
    const deltaY = endY - startY;
    const elapsed = Date.now() - startTime;

    if (elapsed < 400 && Math.abs(deltaX) > 60 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
      if (deltaX > 0 && startX < 40) {
        setShowMobileMenu(true);
      } else if (deltaX < 0 && showMobileMenu) {
        setShowMobileMenu(false);
      }
    }
    touchStartRef.current = null;
  }, [showMobileMenu]);

  const handleLogout = () => {
    logout();
  };

  return (
    <div
      className="flex flex-col h-screen bg-gray-50"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{ touchAction: 'pan-y' }}
    >
      {/* New Booking Toast */}
      {showNewBookingToast && newBookingAlert && (
        <div className="fixed top-4 left-4 right-4 z-50 animate-slide-down">
          <div
            className="text-white p-4 rounded-2xl shadow-lg flex items-center gap-3"
            style={{ background: 'linear-gradient(135deg, #059669, #10b981)', boxShadow: '0 8px 32px rgba(16,185,129,0.3)' }}
          >
            <Bell size={20} className="flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-sm">New Booking!</p>
              <p className="text-xs opacity-90 mt-0.5">
                {newBookingAlert.guestName} - {newBookingAlert.partySize} guests at {newBookingAlert.time}
              </p>
            </div>
            <button
              onClick={() => { setShowNewBookingToast(false); clearNewBookingAlert(); }}
              className="p-1.5 rounded-lg"
              style={{ background: 'rgba(255,255,255,0.15)' }}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* ── Mobile Header ── */}
      <header
        className="lg:hidden flex items-center justify-between px-4 z-40"
        style={{
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          minHeight: '56px',
          position: 'sticky',
          top: 0,
        }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="flex items-center justify-center"
            style={{ width: '44px', height: '44px', marginLeft: '-10px', background: 'none', border: 'none', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
          >
            <Menu size={20} className="text-gray-600" />
          </button>
          <div>
            <h1
              className="font-semibold text-lg leading-tight"
              style={{
                background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {restaurantName || 'Restaurant Panel'}
            </h1>
            <LiveIndicator isConnected={isConnected} />
          </div>
        </div>

        <div className="flex items-center gap-2">
          {pendingBookings.length > 0 && (
            <button
              onClick={() => setShowPendingModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
              style={{ background: 'rgba(245,158,11,0.12)', color: '#d97706', minHeight: '36px' }}
            >
              <Bell size={13} />
              {pendingBookings.length}
            </button>
          )}

          <div className="hidden md:flex items-center gap-2 text-sm">
            <span className="text-gray-500">{staff?.name}</span>
            <span className="px-2 py-0.5 bg-gray-100 rounded-md text-xs text-gray-600 capitalize font-medium">
              {staff?.role}
            </span>
          </div>

          <button
            onClick={handleLogout}
            className="flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
            style={{ width: '40px', height: '40px' }}
            title="Logout"
          >
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {/* ── Desktop Header ── */}
      <header className="hidden lg:flex bg-white border-b border-gray-200 px-4 py-3 items-center justify-between z-40">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="font-semibold text-gray-900 text-lg leading-tight">
              {restaurantName || 'Restaurant Panel'}
            </h1>
            <LiveIndicator isConnected={isConnected} />
          </div>
        </div>
        <div className="flex items-center gap-3">
          {pendingBookings.length > 0 && (
            <button
              onClick={() => setShowPendingModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium hover:bg-yellow-200 transition-colors"
            >
              <Bell size={14} />
              {pendingBookings.length} {t.pending.toLowerCase()}
            </button>
          )}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">{staff?.name}</span>
            <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600 capitalize">
              {staff?.role}
            </span>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Logout"
          >
            <LogOut size={20} />
          </button>
        </div>
      </header>

      {/* ── Mobile Menu Overlay ── */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
            onClick={() => setShowMobileMenu(false)}
          />
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 'min(280px, 85vw)',
              background: '#fff',
              boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="flex items-center justify-center flex-shrink-0"
                    style={{
                      width: '34px',
                      height: '34px',
                      borderRadius: '9px',
                      background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
                    }}
                  >
                    <span className="text-white text-xs font-extrabold" style={{ letterSpacing: '-0.5px' }}>mB</span>
                  </div>
                  <span className="font-semibold text-gray-900 text-sm">{restaurantName}</span>
                </div>
                <button
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center justify-center"
                  style={{ width: '36px', height: '36px', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  <X size={18} className="text-gray-400" />
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">{staff?.name} · <span className="capitalize">{staff?.role}</span></p>
            </div>
            <nav className="p-2">
              {navItems.map((item, i) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setShowMobileMenu(false)}
                    className={`flex items-center gap-3 px-4 rounded-xl transition-all ${
                      isActive
                        ? 'bg-blue-50 text-blue-600 font-medium'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    style={{
                      minHeight: '48px',
                      fontSize: '14px',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <item.icon size={20} strokeWidth={1.8} />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      {/* ── Pending Bookings Modal ── */}
      {showPendingModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-start sm:justify-center sm:pt-20 p-0 sm:p-4">
          <div
            style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
            onClick={() => setShowPendingModal(false)}
          />
          <div className="relative bg-white w-full sm:max-w-lg max-h-[85vh] sm:max-h-[70vh] overflow-hidden shadow-2xl rounded-t-2xl sm:rounded-2xl">
            {/* Handle bar on mobile */}
            <div className="sm:hidden flex justify-center py-2">
              <div className="w-10 h-1 rounded-full bg-gray-200" />
            </div>

            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b bg-yellow-50">
              <div className="flex items-center gap-2">
                <Bell size={20} className="text-yellow-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  {t.pendingBookings || 'Pending Bookings'}
                </h3>
                <span className="px-2 py-0.5 bg-yellow-200 text-yellow-800 rounded-full text-sm font-medium">
                  {pendingBookings.length}
                </span>
              </div>
              <button
                onClick={() => setShowPendingModal(false)}
                className="p-2 hover:bg-yellow-100 rounded-xl transition-colors"
                style={{ minWidth: '40px', minHeight: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            {/* Pending Bookings List */}
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(85vh - 140px)', WebkitOverflowScrolling: 'touch' }}>
              {pendingBookings.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  {t.noPendingBookings || 'No pending bookings'}
                </div>
              ) : (
                <div className="divide-y">
                  {pendingBookings.map(booking => (
                    <div key={booking.id} className="p-4 hover:bg-gray-50">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-900 truncate">
                              {booking.guestName}
                            </span>
                            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">
                              {t.pending}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {(() => {
                                const date = new Date(booking.date);
                                const locale = language === 'pl' ? 'pl-PL' : 'en-US';
                                const day = date.getDate();
                                const weekday = date.toLocaleDateString(locale, { weekday: 'long' });
                                const month = date.toLocaleDateString(locale, { month: 'long' });
                                const capitalizedWeekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);
                                const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);
                                return `${day} ${capitalizedMonth}, ${capitalizedWeekday}`;
                              })()}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {booking.time}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users size={14} />
                              {booking.partySize} {booking.partySize === 1 ? t.guest : t.guests}
                            </span>
                          </div>
                          {booking.tableName && (
                            <div className="text-sm text-gray-500 mt-1">
                              <Table2 size={14} className="inline mr-1" />
                              {booking.tableName}
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            onClick={() => handlePendingAction(booking.id, 'confirm')}
                            disabled={actionLoading === booking.id}
                            className="flex items-center justify-center gap-1.5 flex-1 sm:flex-none px-4 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
                            style={{ minHeight: '44px' }}
                          >
                            {actionLoading === booking.id ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <CheckCircle size={16} />
                            )}
                            {t.confirm}
                          </button>
                          <button
                            onClick={() => handlePendingAction(booking.id, 'cancel')}
                            disabled={actionLoading === booking.id}
                            className="flex items-center justify-center gap-1.5 px-4 bg-red-50 text-red-600 rounded-xl text-sm font-medium hover:bg-red-100 disabled:opacity-50 transition-colors"
                            style={{ minHeight: '44px' }}
                          >
                            <XCircle size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-3 border-t bg-gray-50">
              <button
                onClick={() => { setShowPendingModal(false); navigate(`${basePath}/bookings?status=pending`); }}
                className="w-full rounded-xl text-sm text-blue-600 font-medium hover:bg-blue-50 transition-colors"
                style={{ minHeight: '44px' }}
              >
                {t.viewAll} →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Main Content ── */}
      <main className="flex-1 overflow-auto pb-20 lg:pb-4">
        <Outlet context={{ bookings, isConnected, onStatusChange: updateBookingStatus, onDelete: deleteBooking }} />
      </main>

      {/* ── Bottom Navigation - Mobile ── */}
      <nav
        className="fixed bottom-0 left-0 right-0 lg:hidden z-40"
        style={{
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderTop: '1px solid rgba(0,0,0,0.06)',
          paddingTop: '6px',
          paddingBottom: 'max(6px, env(safe-area-inset-bottom))',
        }}
      >
        <div className="flex justify-around max-w-md mx-auto px-2">
          {navItems.filter(item => item.showMobile).map(item => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className="flex flex-col items-center justify-center relative"
                style={{
                  minWidth: '52px',
                  minHeight: '48px',
                  color: isActive ? '#2563eb' : '#9ca3af',
                  textDecoration: 'none',
                  WebkitTapHighlightColor: 'transparent',
                  transition: 'color 0.15s ease',
                }}
              >
                <item.icon size={22} strokeWidth={isActive ? 2 : 1.8} />
                <span style={{ fontSize: '9px', marginTop: '3px', fontWeight: isActive ? 600 : 400 }}>
                  {item.label}
                </span>
                {/* Active indicator dot */}
                {isActive && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '-2px',
                      width: '4px',
                      height: '4px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
                    }}
                  />
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* ── Desktop Sidebar ── */}
      <aside className="hidden lg:flex fixed left-0 top-[61px] bottom-0 w-56 bg-white border-r border-gray-200 flex-col">
        <nav className="flex-1 p-3">
          {navItems.map(item => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl mb-1 transition-colors ${
                  isActive
                    ? 'bg-blue-50 text-blue-600 font-medium'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <item.icon size={20} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t">
          <div className="text-xs text-gray-400">Logged in as</div>
          <div className="text-sm font-medium text-gray-700">{staff?.name}</div>
          <div className="text-xs text-gray-400 capitalize">{staff?.role}</div>
        </div>
      </aside>

      {/* ── Styles ── */}
      <style>{`
        @media (min-width: 1024px) {
          main {
            margin-left: 14rem;
          }
        }
        @keyframes slide-down {
          from { transform: translateY(-100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
        @media (pointer: coarse) {
          button, a, [role="button"] {
            -webkit-tap-highlight-color: transparent;
          }
        }
      `}</style>
    </div>
  );
}

export default function RestaurantLayout() {
  return (
    <RestaurantTranslationProvider>
      <RestaurantLayoutInner />
    </RestaurantTranslationProvider>
  );
}
